import { cloneElement, useContext } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { position as positionStyles, layout } from 'styled-system';
import { motion, AnimatePresence } from 'motion/react';
import UIContext from '../../ui/ui-context';
import breakpoint, { getZindexByType } from '../../utils/style-utils';
import useLockBodyScroll from '~/utils/lock-body-scroll';

const sidebarTop = ({ hasHeader, hasProductNav, hasStaticHeader, ignoreSubheaderHeight }) => {
	const rgh = hasStaticHeader ? `var(--regulationHeight)` : '0px';
	const hdh = hasHeader ? `var(--headerHeight)` : '0px';
	const prh = hasProductNav && !ignoreSubheaderHeight ? `var(--subHeaderHeight)` : '0px';
	const positionTop = `calc(${rgh} + ${hdh} + ${prh})`;
	return positionTop;
};

const modalTop = ({ setTopToZero, small, regOnly, hasStaticHeader, ...rest }) => {
	if (setTopToZero) return '0px';
	if (regOnly)
		if (hasStaticHeader) {
			return `var(--regulationHeight)`;
		} else {
			return '0px';
		}
	if (small) {
		if (hasStaticHeader) {
			return `var(--regulationHeight)`;
		}
		return '0px';
	}
	return sidebarTop({ hasStaticHeader, ...rest });
};

const getWrapperDynamicWidth = (showSidebar, notificationPopup) => {
	if (notificationPopup) {
		if (showSidebar) {
			return 'calc(100% - 375px)';
		}
		return 'calc(100%)';
	}
	return '100%';
};

const variantsFromBottom = {
	enter: {
		y: 0,
		transitionEnd: { y: 0 },
		transition: {
			type: 'spring',
			damping: 16,
			duration: 0.2
		}
	},
	exit: {
		y: '100%',
		transition: {
			type: 'spring',
			damping: 16,
			duration: 0.2
		}
	}
};

const ModalStyles = styled(motion.div)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	background-color: ${props =>
		props.blurBg
			? 'rgba(var(--modal-wrapper-dark-bg, var(--base-11)), 0.85)'
			: 'var(--sidebar-bg, rgba(var(--base-11), 1))'};
	z-index: ${getZindexByType('modal')};
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	align-items: stretch;
	flex-flow: column nowrap;
	overflow: hidden;
	${positionStyles}
`;

const ModalView = styled.div`
	background-color: var(--sidebar-bg, rgba(var(--base-11), 1));
	width: 100%;
	height: 100%;
	opacity: 1;
	visibility: visible;
	left: 0;
	right: 0;
	top: 0;
	position: ${props => (props.position ? props.position : 'sticky')};
	z-index: ${getZindexByType('modal-view')};
`;

const SimpleModal = ({ position = 'fixed', ...rest }) => {
	return (
		<ModalStyles
			variants={variantsFromBottom}
			animate={rest.open ? 'enter' : 'exit'}
			position={position}
			initial="exit"
			{...rest}
		>
			{rest.children}
		</ModalStyles>
	);
};

const Wrapper = styled.div`
	width: 100%;
	bottom: 0;
	height: ${props => (props.customHeight ? `150px` : `auto`)};
	${props => (props.notificationPopup ? `left: 0;` : `right: 0;`)}
	${props => props.notificationPopup && `overflow-y: scroll;`}
	top: ${props => (props.bgFitted ? 'calc(100% - 197px)' : modalTop(props))};
	z-index: ${props =>
		props.notificationPopup ? getZindexByType('inbox-notification-slider') : getZindexByType('modal')};
	background-color: ${props => (props.blurBg ? 'rgba(var(--base-11), 0.85)' : 'none')};
	${breakpoint.lg`
		width: ${({ withinSidebar, bgFitted, notificationPopup, showSidebar }) =>
			withinSidebar || bgFitted ? '375px' : getWrapperDynamicWidth(showSidebar, notificationPopup)};
		`}
	${positionStyles}
  ${layout}
`;

const MotionWrapper = motion.create(Wrapper);
const animationProps = {
	variants: variantsFromBottom,
	initial: 'exit',
	animate: 'enter',
	exit: 'exit'
};

export const UnconnectedModal = ({
	show,
	onToggleShow,
	children,
	small,
	blurBg,
	setTopToZero = false,
	bgFitted,
	notificationPopup = false,
	instant = false, // Use it to bypass animation
	lockBodyScroll = false, // Use it to lock body scroll
	customHeight,
	position = 'fixed',
	...rest
}) => {
	const { withinSidebar, hasStaticHeader, hasHeader, hasProductNav, showSidebar } = useContext(UIContext);
	useLockBodyScroll(lockBodyScroll);

	return (
		<AnimatePresence>
			{show && (
				<MotionWrapper
					{...(instant ? {} : animationProps)}
					withinSidebar={withinSidebar}
					hasStaticHeader={hasStaticHeader}
					hasHeader={hasHeader}
					hasProductNav={hasProductNav} // yes, apparently this is correct
					key="modal-x"
					small={small}
					blurBg={blurBg}
					setTopToZero={setTopToZero}
					notificationPopup={notificationPopup}
					bgFitted={bgFitted}
					showSidebar={showSidebar}
					customHeight={customHeight}
					position={position}
					{...rest}
				>
					{cloneElement(children, {
						...(onToggleShow ? { onToggleShow } : {})
					})}
				</MotionWrapper>
			)}
		</AnimatePresence>
	);
};

const mapStateToProps = ({ browser }) => ({
	small: browser.is.small
});

const Modal = connect(mapStateToProps)(UnconnectedModal);

export { Modal, SimpleModal, ModalView, variantsFromBottom };

export default Modal;
